import { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CaretRightFilled from "@ant-design/icons/CaretRightFilled";
import CloseOutlined from "@ant-design/icons/CloseOutlined";
import { v4 as uuidv4 } from "uuid";

import ModalContainer from "components/ModalContainer";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import useDataSourceManager from "hooks/useDataSourceManager";

const formatNumber = (value) => new Intl.NumberFormat("en-US").format(value || 0);

const TransferCreditsModal = ({
  open,
  onClose,
  organization,
  treasuryBalance,
  entId,
  defaultDirection,
  onCompleted,
}) => {
  const theme = useTheme();
  const { dataMap, sendRequest } = useDataSourceManager();
  const [requestId] = useState(uuidv4());
  const [direction, setDirection] = useState(defaultDirection);
  const [amount, setAmount] = useState("");
  const [step, setStep] = useState("form");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const numericAmount = Number(amount);
  const maxAmount = direction === "disburse" ? treasuryBalance : organization?.balance || 0;
  const isAmountValid = Number.isFinite(numericAmount) && numericAmount > 0 && numericAmount <= maxAmount;
  const amountLabel = direction === "disburse" ? "Disburse Amount / Credits" : "Withdraw Amount / Credits";
  const amountError = amount && !isAmountValid
    ? `Amount cannot exceed ${formatNumber(maxAmount)} credits.`
    : error;

  const getInputSx = (isInvalid = false) => ({
    "& .MuiOutlinedInput-root": {
      height: "31px",
      backgroundColor: theme.palette.primary.transparent,
      borderRadius: theme.shape.customBorderRadius[1],
      "& .MuiOutlinedInput-notchedOutline": {
        border: `1px solid ${isInvalid ? theme.palette.primary.redPrimary : theme.palette.primary.borderGreyPrimary}`,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        border: `1px solid ${isInvalid ? theme.palette.primary.redPrimary : theme.palette.primary.mainHover}`,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: `1px solid ${isInvalid ? theme.palette.primary.redPrimary : theme.palette.primary.main}`,
      },
    },
    "& .MuiOutlinedInput-input": {
      fontSize: theme.typography.customFontSize[1],
    },
    "& .MuiOutlinedInput-input::placeholder": {
      color: theme.palette.primary.greyThird,
      fontSize: theme.typography.customFontSize[1],
      opacity: 1,
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
      color: isInvalid ? theme.palette.primary.redPrimary : theme.palette.primary.greyPrimary,
      fontSize: "0.75rem",
    },
  });

  useEffect(() => {
    if (!open) return;

    setDirection(defaultDirection);
    setAmount(String(
      defaultDirection === "disburse"
        ? treasuryBalance
        : organization?.balance || 0,
    ));
    setStep("form");
    setError("");
    setSubmitting(false);
  }, [open, defaultDirection, organization?.id, organization?.balance, treasuryBalance]);

  useEffect(() => {
    const response = dataMap[requestId];

    if (!response) return;

    if (response.status !== "success") {
      setError(response.message || "Unable to complete the transfer.");
      setSubmitting(false);
      return;
    }

    onCompleted();
    onClose();
  }, [dataMap, requestId, onCompleted, onClose]);

  const handleNext = () => {
    if (!isAmountValid) {
      setError(`Amount cannot exceed ${formatNumber(maxAmount)} credits.`);
      return;
    }

    setError("");
    setStep("confirm");
  };

  const handleConfirm = () => {
    if (submitting) return;

    setError("");
    setSubmitting(true);
    sendRequest(
      "/ents/:entId/orgs/:orgId/license/allocate.update",
      requestId,
      { entId, orgId: organization.id },
      {
        action: direction,
        amount: numericAmount,
      },
    );
  };

  if (!open || !organization) return null;

  const isDisburse = direction === "disburse";
  const fromName = isDisburse ? "MSP Treasury" : organization.name;
  const toName = isDisburse ? organization.name : "MSP Treasury";
  const fromBalance = isDisburse ? treasuryBalance - numericAmount : organization.balance - numericAmount;
  const toBalance = isDisburse ? organization.balance + numericAmount : treasuryBalance + numericAmount;

  return (
    <ModalContainer display={open} modalWidth="600px">
      <Box sx={{ position: "relative" }}>
        <Stack spacing={2.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography
              sx={{
                color: theme.palette.primary.main,
                fontSize: theme.typography.customFontSize[4],
                fontWeight: theme.typography.customFontWeight[1],
              }}
            >
              Transfer Credits
            </Typography>
            <IconButton onClick={onClose} aria-label="Close transfer credits" sx={{ p: 0.5 }}>
              <CloseOutlined style={{ fontSize: "18px", color: theme.palette.primary.greyPrimaryTransparency }} />
            </IconButton>
          </Stack>

          <Box sx={{ overflowY: "auto", maxHeight: "75vh" }}>
            <Stack spacing={2}>
              {step === "form" ? (
                <>
                  <RadioGroup
                    value={direction}
                    onChange={(event) => {
                      const nextDirection = event.target.value;
                      setDirection(nextDirection);
                      setAmount(String(
                        nextDirection === "disburse"
                          ? treasuryBalance
                          : organization.balance,
                      ));
                      setError("");
                    }}
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        color: theme.palette.primary.greyPrimary,
                        fontSize: theme.typography.customFontSize[1],
                      },
                    }}
                  >
                    <FormControlLabel value="disburse" control={<Radio size="small" />} label="Disburse (MSP Treasury → Organization)" />
                    <FormControlLabel value="withdraw" control={<Radio size="small" />} label="Withdraw (Organization → MSP Treasury)" />
                  </RadioGroup>

                  <Divider />
                  <Box sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: "12px" }}>
                    <Stack spacing={1.25}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">MSP Treasury Balance</Typography>
                        <Typography variant="body2" fontWeight="bold">{formatNumber(treasuryBalance)} / Credits</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">{isDisburse ? "Recipient" : "Source"}</Typography>
                        <Typography variant="body2" fontWeight="bold">{organization.name}</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">This Org's Current Balance</Typography>
                        <Typography variant="body2" fontWeight="bold">{formatNumber(organization.balance)} / Credits</Typography>
                      </Stack>
                    </Stack>
                  </Box>

                  <Stack spacing={0.75}>
                    <Typography sx={{ color: theme.palette.primary.main, fontSize: theme.typography.customFontSize[1], fontWeight: theme.typography.customFontWeight[1] }}>
                      {amountLabel} (max {formatNumber(maxAmount)})
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={amount}
                      error={Boolean(amountError)}
                      sx={getInputSx(Boolean(amountError))}
                      onChange={(event) => {
                        setAmount(event.target.value);
                        setError("");
                      }}
                      inputProps={{ min: 1, max: maxAmount }}
                    />
                  </Stack>
                  <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 1 }}>
                    <OutlineButton width="120px" height="36px" onClick={onClose} sx={{ borderRadius: "20px" }}>Back</OutlineButton>
                    <SolidButton width="120px" height="36px" disabled={!isAmountValid} onClick={handleNext} sx={{ borderRadius: "20px" }}>Next</SolidButton>
                  </Stack>
                </>
              ) : (
                <>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CaretRightFilled style={{ color: theme.palette.primary.main, fontSize: "14px" }} />
                    <Typography sx={{ color: theme.palette.primary.greyPrimary, fontSize: theme.typography.customFontSize[2], fontWeight: theme.typography.customFontWeight[1] }}>
                      Please confirm the following summary
                    </Typography>
                  </Stack>
                  <Box sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: "12px" }}>
                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <Typography variant="body2" color="text.secondary">From</Typography>
                        <Typography variant="body2" fontWeight="bold" textAlign="right">{fromName} ({formatNumber(fromBalance)} credits after)</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <Typography variant="body2" color="text.secondary">To</Typography>
                        <Typography variant="body2" fontWeight="bold" textAlign="right">{toName} ({formatNumber(toBalance)} credits after)</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <Typography variant="body2" color="text.secondary">Amount</Typography>
                        <Typography variant="body2" fontWeight="bold">{formatNumber(numericAmount)} Credits</Typography>
                      </Stack>
                    </Stack>
                  </Box>
                  {error && <Typography variant="body2" color="error">{error}</Typography>}
                  <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 1 }}>
                    <OutlineButton width="120px" height="36px" disabled={submitting} onClick={() => setStep("form")} sx={{ borderRadius: "20px" }}>Back</OutlineButton>
                    <SolidButton width="120px" height="36px" disabled={submitting} onClick={handleConfirm} sx={{ borderRadius: "20px" }}>Confirm</SolidButton>
                  </Stack>
                </>
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>
    </ModalContainer>
  );
};

export default TransferCreditsModal;
